import { hydrateChainRegistry, registry } from "@authe/fe-commons/chains";
import { LocalAgent, type Snapshot, type TraceConfig } from "./client";

/**
 * The chains a reader's recorded runs carry rows for (a protocol network's own
 * Ethereum chain, a NEAR localnet), registered so the page names them and
 * formats their native asset instead of printing raw units. Only chains the
 * registry does not know: a reader never replaces a chain the app resolved.
 * Checked on every snapshot, so a later complete hydration that dropped them
 * is repaired on the next poll.
 */
export function registerReaderChains(rows: readonly unknown[] | undefined): string[] {
  const missing = (rows ?? []).filter((row) => {
    const caip2 = row && typeof row === "object" ? (row as { caip2?: unknown }).caip2 : undefined;
    return typeof caip2 === "string" && !registry.getByCaip2(caip2);
  });
  return missing.length ? [...hydrateChainRegistry(missing, { replace: false }).added] : [];
}

/**
 * The one-click pairing link `bc dashboard` prints:
 * `/local-networks#reader=<loopback url>&code=<pairing code>`.
 *
 * In the FRAGMENT, so the code never leaves the browser: a fragment is not
 * sent to the dashboard's host, and the page drops it from the address bar
 * and history before it connects. Anything else in the hash is ignored.
 */
export function pairingFromHash(hash: string): { url: string; code: string } | null {
  const params = new URLSearchParams(hash.replace(/^#/, ""));
  const url = params.get("reader");
  const code = params.get("code");
  return url && code ? { url, code } : null;
}

/**
 * The pairing link still to be followed, kept in memory (never storage) until
 * a reader answers it.
 *
 * The reader session is remounted once the signed-in identity resolves, a
 * moment after first paint; a connection started by the first session was
 * disposed with it, and the link had already left the address bar — so a
 * link opened on a cold page paired nothing. Whichever session asks next
 * finds the link here and connects; a reload drops it, as it drops the code.
 */
let pendingPairing: { url: string; code: string } | null = null;

export function pendingPairingLink(hash: string): { url: string; code: string } | null {
  const fromHash = pairingFromHash(hash);
  if (fromHash) pendingPairing = fromHash;
  return pendingPairing;
}

export function pairingLinkFollowed(): void {
  pendingPairing = null;
}

export const LOCAL_READER_DEFAULTS = {
  pollMs: 2000,
  timeoutMs: 10000,
} as const;
export type LocalReaderPolicy = { pollMs: number; timeoutMs: number };
export type ReaderState = {
  connected: boolean;
  busy: boolean;
  snapshot: Snapshot | null;
  error: string;
  errorSource: "reader" | "connection" | null;
  origin: string | null;
};
const EMPTY: ReaderState = {
  connected: false,
  busy: false,
  snapshot: null,
  error: "",
  errorSource: null,
  origin: null,
};

/** One tab-owned connection, shared by Debug and the workspace. No credentials
 * or snapshots in browser storage. Disposal aborts every outstanding request. */
export class LocalReaderSession {
  private value = EMPTY;
  private listeners = new Set<() => void>();
  private agent: LocalAgent | null = null;
  private controller = new AbortController();
  private timer?: ReturnType<typeof setTimeout>;
  private generation = 0;
  readonly policy: LocalReaderPolicy;
  constructor(policy: Partial<LocalReaderPolicy> = {}) {
    this.policy = {
      pollMs: Math.max(250, policy.pollMs ?? LOCAL_READER_DEFAULTS.pollMs),
      timeoutMs: Math.max(
        500,
        policy.timeoutMs ?? LOCAL_READER_DEFAULTS.timeoutMs
      ),
    };
  }
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
  getSnapshot = () => this.value;
  private publish(next: Partial<ReaderState>) {
    this.value = { ...this.value, ...next };
    this.listeners.forEach((listener) => listener());
  }
  private signal() {
    return AbortSignal.any([
      this.controller.signal,
      AbortSignal.timeout(this.policy.timeoutMs),
    ]);
  }
  disconnect = () => {
    this.generation++;
    this.controller.abort();
    this.controller = new AbortController();
    clearTimeout(this.timer);
    this.agent = null;
    this.value = EMPTY;
    this.listeners.forEach((listener) => listener());
  };
  connect = async (origin: string, code: string) => {
    this.disconnect();
    try {
      this.agent = new LocalAgent(origin, code.trim());
    } catch (error) {
      this.publish({ error: this.explain(error), errorSource: "connection" });
      return;
    }
    // Keep the validated endpoint and credential in this tab only, so an
    // initial network/permission failure can be retried after the link's
    // fragment has been removed. Disconnect still discards both.
    this.publish({ origin: this.agent.origin });
    await this.pair(this.generation);
  };
  private async pair(generation: number) {
    const agent = this.agent;
    if (!agent || generation !== this.generation) return;
    this.publish({ busy: true });
    try {
      const snapshot = await agent.request<Snapshot>("snapshot", this.signal());
      if (generation !== this.generation) return;
      this.agent = agent;
      registerReaderChains(snapshot.chains);
      this.publish({
        connected: true,
        busy: false,
        snapshot,
        origin: agent.origin,
        error: snapshot.error ?? "",
        errorSource: snapshot.state === "error" ? "reader" : null,
      });
      pairingLinkFollowed();
      this.schedule(generation);
    } catch (error) {
      if (generation === this.generation)
        this.publish({ busy: false, error: this.explain(error), errorSource: "connection" });
    }
  }
  private explain(error: unknown) {
    return `${
      error instanceof Error ? error.message : error
    }. Check the reader and this browser's local-device access permission.`;
  }
  private schedule(generation: number) {
    this.timer = setTimeout(
      () => void this.refresh(generation),
      this.policy.pollMs
    );
  }
  private async refresh(generation: number) {
    if (!this.agent || generation !== this.generation) return;
    try {
      const snapshot = await this.agent.request<Snapshot>(
        "snapshot",
        this.signal()
      );
      if (generation !== this.generation) return;
      if (snapshot.session_id !== this.value.snapshot?.session_id)
        throw new Error("Reader session changed; disconnect and pair again");
      registerReaderChains(snapshot.chains);
      this.publish({
        snapshot,
        error:
          snapshot.state === "error" ? snapshot.error ?? "Reader failed" : "",
        errorSource: snapshot.state === "error" ? "reader" : null,
      });
    } catch (error) {
      if (generation === this.generation)
        this.publish({ error: this.explain(error), errorSource: "connection" });
    } finally {
      if (generation === this.generation) this.schedule(generation);
    }
  }
  retry = async () => {
    if (!this.agent || this.value.busy) return;
    const generation = ++this.generation;
    clearTimeout(this.timer);
    this.controller.abort();
    this.controller = new AbortController();
    // Keep the stale state until a successful read verifies the connection.
    this.publish({ busy: true });
    try {
      if (this.value.connected) await this.refresh(generation);
      else await this.pair(generation);
    } finally {
      if (generation === this.generation) this.publish({ busy: false });
    }
  };
  configure = async (config: TraceConfig) => {
    if (!this.agent || !this.value.connected) return;
    const generation = ++this.generation;
    clearTimeout(this.timer);
    this.controller.abort();
    this.controller = new AbortController();
    this.publish({ busy: true, error: "", errorSource: null });
    try {
      await this.agent.request("trace", this.signal(), config);
      if (generation === this.generation) await this.refresh(generation);
    } catch (error) {
      if (generation === this.generation) {
        this.publish({ error: this.explain(error), errorSource: "connection" });
        this.schedule(generation);
      }
    } finally {
      if (generation === this.generation) this.publish({ busy: false });
    }
  };
  inspect = async (id: string) => {
    if (!this.agent || !this.value.connected) throw new Error("Connect a local reader first");
    return this.agent.request(
      "transactions/" + encodeURIComponent(id),
      this.signal()
    );
  };
}
